import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Cloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Statuses that we consider "final" — once we see one of these we stop the
// automatic polling loop to avoid burning quota against FIC.
const FINAL_STATUS_RE = /deliver|consegnat|scart|reject|error|fail|not_delivered|non_recapit/i;

function parseEvent(n) {
  const p = n.payload || {};
  const d = p.data || {};
  return {
    when: n.received_at,
    type: p.type || p.event || "notification",
    status: d.ei_status || d.status || "unknown",
  };
}

export default function SdiTimeline({ invoiceId }) {
  const [items, setItems] = useState([]);
  const [ficDocId, setFicDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/fic-notifications`);
      setItems(data.notifications || []);
      setFicDocId(data.fic_document_id ?? null);
      return data;
    } catch (e) {
      console.warn("[SdiTimeline] fetch failed", e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  const refreshStatus = useCallback(async ({ silent = false } = {}) => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/refresh-fic-status`);
      await fetchNotifications();
      if (!silent) toast.success(`Stato SDI: ${data.ei_status || "aggiornato"}`);
      return data;
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || "Impossibile aggiornare lo stato");
      }
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [invoiceId, fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh FIC status on mount only. Dal Feb 2026 il push del cambio
  // stato SDI arriva via webhook OpenAPI (`/api/sdi/webhook/openapi`) →
  // niente polling ricorrente. L'utente può cliccare "Aggiorna" (già in UI)
  // se vuole forzare un refetch manuale.
  useEffect(() => {
    if (!ficDocId) return;
    // Solo un refresh on-mount se non siamo già in stato finale.
    const latest = items.length ? parseEvent(items[items.length - 1]) : null;
    const isFinal = latest && FINAL_STATUS_RE.test(latest.status || "");
    if (isFinal) return;
    refreshStatus({ silent: true });
  }, [ficDocId]);

  if (loading) return null;
  if (!ficDocId && !items.length) return null;

  return (
    <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5 md:p-6 mb-6" data-testid="sdi-timeline">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold">
          <span className="w-6 h-px bg-[#1D4ED8] inline-block mr-2 align-middle" />
          Timeline SDI
        </div>
        <span className="text-[10px] text-[#737373] ml-auto font-mono">
          {items.length} eventi
        </span>
        {ficDocId && (
          <button
            type="button"
            onClick={() => refreshStatus({ silent: false })}
            disabled={refreshing}
            data-testid="sdi-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#E8E4D9] bg-white text-[11px] font-semibold text-[#1D4ED8] hover:bg-[#F5F2EA] disabled:opacity-60 transition-colors"
          >
            {refreshing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            Aggiorna stato
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-[#E8E4D9] bg-[#F5F2EA] p-4 text-sm text-[#4A4A4A] flex items-center gap-2"
          data-testid="sdi-timeline-empty"
        >
          <Cloud className="w-4 h-4 text-[#1D4ED8]" />
          Nessun evento ancora. Premi &quot;Aggiorna stato&quot; oppure attendi la notifica automatica dallo SDI.
        </div>
      ) : (
        <ol className="space-y-3 relative before:content-[''] before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-[#E8E4D9]">
          {items.map((n, i) => {
            const e = parseEvent(n);
            const tone = /deliver|consegnat|accept/i.test(e.status)
              ? { dot: "bg-[#059669]", text: "text-[#059669]" }
              : /reject|scart|error|fail/i.test(e.status)
              ? { dot: "bg-red-500", text: "text-red-600" }
              : { dot: "bg-[#1D4ED8]", text: "text-[#1D4ED8]" };
            return (
              <li key={n.id || i} className="relative pl-7">
                <span className={`absolute left-0 top-1.5 w-[18px] h-[18px] rounded-full ${tone.dot} border-4 border-white shadow`} />
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className={`font-display font-bold text-sm ${tone.text} uppercase tracking-wider`}>
                    {e.status}
                  </span>
                  <span className="text-xs text-[#737373]">{e.type}</span>
                  <span className="text-xs text-[#737373] font-mono ml-auto">
                    {(e.when || "").slice(0, 19).replace("T", " ")}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
